import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import chipAppearance from './appearance';
import HoldHint from './HoldHint';
import RenameForm from './RenameForm';

const HOLD_DURATION = 600; // ms — see design handoff
// Don't tint the chip until the press lasts past a typical click. Below this,
// release navigates and we shouldn't have flashed red.
const PRESS_VISUAL_THRESHOLD = 0.25; // ~150ms of HOLD_DURATION

const PROPOSED_HINT = 'Proposed tag — no public match yet';

function TagChip({
  tag, otherTagNames, onRename, onMerge, onOpen, style, synthetic, highlight,
}) {
  const theme = useTheme();
  const brandRed = theme.palette.primary.main;
  const successGreen = theme.palette.success.main;
  // Synthetic chips (e.g. the "untagged" pseudo-tag) are never editable.
  const renameEnabled = typeof onRename === 'function' && !synthetic;
  const showCount = tag.count != null;
  const [mode, setMode] = useState('idle'); // idle | pressing | editing
  const [pressProgress, setPressProgress] = useState(0);
  const [inputVal, setInputVal] = useState(tag.name);
  const [showHint, setShowHint] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [justRenamed, setJustRenamed] = useState(false);

  const progressTimer = useRef(null);
  const inputRef = useRef(null);
  const startTime = useRef(null);
  const blurTimer = useRef(null);
  const flashTimer = useRef(null);

  useEffect(() => () => {
    if (progressTimer.current) cancelAnimationFrame(progressTimer.current);
    if (blurTimer.current) clearTimeout(blurTimer.current);
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  useEffect(() => {
    if (mode === 'editing' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [mode]);

  const trimmed = inputVal.trim().toLowerCase();
  const isDirty = trimmed.length > 0 && trimmed !== tag.name;
  const conflictName = (isDirty && otherTagNames && otherTagNames.has(trimmed))
    ? trimmed : null;

  const cancelEdit = useCallback(() => {
    setMode('idle');
    setInputVal(tag.name);
    setShowMerge(false);
  }, [tag.name]);

  const commitRename = useCallback((name) => {
    const isMerge = otherTagNames && otherTagNames.has(name);
    setMode('idle');
    setShowMerge(false);
    setJustRenamed(true);
    if (isMerge && onMerge) {
      onMerge(tag.id, name);
    } else {
      onRename(tag.id, name);
    }
    flashTimer.current = setTimeout(() => setJustRenamed(false), 900);
  }, [tag.id, otherTagNames, onRename, onMerge]);

  // What Enter and the tick both do: nothing to commit, needs a merge, or go.
  const submit = () => {
    if (!trimmed || trimmed === tag.name) { cancelEdit(); return; }
    if (conflictName) { setShowMerge(true); return; }
    commitRename(trimmed);
  };

  const startPress = (e) => {
    if (!renameEnabled) return;
    e.preventDefault();
    if (mode === 'editing') return;
    setShowHint(true);
    startTime.current = Date.now();
    setMode('pressing');

    const tick = () => {
      const elapsed = Date.now() - startTime.current;
      const progress = Math.min(elapsed / HOLD_DURATION, 1);
      setPressProgress(progress);
      if (progress < 1) {
        progressTimer.current = requestAnimationFrame(tick);
      } else {
        setMode('editing');
        setShowHint(false);
        setPressProgress(0);
        setInputVal(tag.name);
      }
    };
    progressTimer.current = requestAnimationFrame(tick);
  };

  // Released before the hold threshold = treat as a click → open the tag view.
  // Side effects (onOpen, navigation) must run outside the state updater —
  // React StrictMode invokes state updaters twice and would fire navigate twice.
  const endPress = (clickIntent) => {
    if (mode !== 'pressing') return;
    if (progressTimer.current) cancelAnimationFrame(progressTimer.current);
    setMode('idle');
    setPressProgress(0);
    setShowHint(false);
    if (clickIntent && onOpen) onOpen(tag.id, tag.name);
  };

  const handlePlainClick = () => {
    if (onOpen) onOpen(tag.id, tag.name);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleInputChange = (e) => {
    setInputVal(e.target.value.toLowerCase());
    setShowMerge(false);
  };

  const handleBlur = () => {
    if (showMerge) return;
    if (blurTimer.current) clearTimeout(blurTimer.current);
    blurTimer.current = setTimeout(cancelEdit, 150);
  };

  if (mode === 'editing') {
    return (
      <RenameForm
        tag={tag}
        value={inputVal}
        isDirty={isDirty}
        conflictName={conflictName}
        showMerge={showMerge}
        inputRef={inputRef}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onSubmit={submit}
        onCancel={cancelEdit}
        onConfirmMerge={() => commitRename(trimmed)}
        onDismissMerge={() => {
          setShowMerge(false);
          if (inputRef.current) inputRef.current.focus();
        }}
      />
    );
  }

  const isHolding = mode === 'pressing' && pressProgress >= PRESS_VISUAL_THRESHOLD;
  const ringColor = `conic-gradient(${brandRed} ${pressProgress * 360}deg, transparent 0deg)`;
  const chip = chipAppearance({
    synthetic, isHolding, highlight, justRenamed, brandRed, successGreen,
  });

  return (
    <Box
      sx={{
        position: 'relative', display: 'inline-flex', minWidth: 0, maxWidth: '100%', ...style,
      }}
      onMouseEnter={() => renameEnabled && setShowHint(true)}
      onMouseLeave={() => { setShowHint(false); endPress(false); }}
    >
      {showHint && mode === 'idle' && renameEnabled && <HoldHint />}

      <Box sx={{
        position: 'relative', display: 'inline-flex', minWidth: 0, maxWidth: '100%',
      }}>
        {isHolding && (
          <Box sx={{
            position: 'absolute',
            inset: '-3px',
            borderRadius: '13px',
            background: ringColor,
            zIndex: 0,
            pointerEvents: 'none',
          }}
          />
        )}
        <Box
          onMouseDown={renameEnabled ? startPress : undefined}
          onMouseUp={renameEnabled ? () => endPress(true) : undefined}
          onTouchStart={renameEnabled ? startPress : undefined}
          onTouchEnd={renameEnabled ? () => endPress(true) : undefined}
          onClick={!renameEnabled ? handlePlainClick : undefined}
          title={highlight === 'proposed' ? PROPOSED_HINT : undefined}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
            maxWidth: '100%',
            border: chip.border,
            borderRadius: '10px',
            padding: '2px 8px',
            fontSize: '0.85rem',
            fontWeight: 400,
            fontStyle: synthetic ? 'italic' : 'normal',
            color: chip.color,
            textTransform: 'lowercase',
            letterSpacing: '0.5px',
            cursor: onOpen ? 'pointer' : 'default',
            background: chip.background,
            transition: 'border-color 0.1s, color 0.1s, background 0.1s',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            animation: chip.animation,
          }}
        >
          {synthetic && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ flexShrink: 0, opacity: 0.75 }}
            >
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
              <line x1="3" y1="3" x2="21" y2="21" />
            </svg>
          )}
          <Box
            component="span"
            title={highlight === 'proposed' ? undefined : tag.name}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {tag.name}
          </Box>
          {showCount && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: chip.countColor,
                flexShrink: 0,
                transition: 'color 0.1s',
              }}
            >
              {tag.count.toLocaleString('en-US')}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(TagChip, (prev, next) => (
  prev.tag.id === next.tag.id
  && prev.tag.name === next.tag.name
  && prev.tag.count === next.tag.count
  && prev.synthetic === next.synthetic
  && prev.highlight === next.highlight
  && prev.otherTagNames === next.otherTagNames
  && prev.onRename === next.onRename
  && prev.onMerge === next.onMerge
  && prev.onOpen === next.onOpen
  && prev.style === next.style
));
